// SmoothCollapse.tsx
import styled from 'styled-components';
import React from 'react';
import { FlexColumn } from '@make-software/cspr-design';

// Get FlexColumn props without ref collisions
type FlexColumnProps = React.ComponentPropsWithoutRef<typeof FlexColumn>;

// Our public props: same as FlexColumn + isOpen
type SmoothCollapseProps = Omit<FlexColumnProps, 'ref'> & {
  isOpen: boolean;
};

// Use a transient prop so it won't end up on the DOM node
const Collapse = styled(FlexColumn)<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '1000px' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    max-height 300ms ease,
    opacity 200ms ease;
  will-change: max-height, opacity;
`;

export const SmoothCollapse = React.forwardRef<HTMLDivElement, SmoothCollapseProps>(
  ({ isOpen, children, className, innerRef, ...rest }, ref) => {
    // Prefer explicit innerRef from the design system, fall back to React ref
    const finalRef = (innerRef as React.Ref<HTMLDivElement>) ?? ref;

    // Avoid passing innerRef down twice
    const { innerRef: _omit, ...props } = rest as any;

    return (
      <Collapse
        {...props}
        className={className}
        aria-hidden={!isOpen}
        $open={isOpen}
        ref={finalRef}
      >
        {children}
      </Collapse>
    );
  }
);

SmoothCollapse.displayName = 'SmoothCollapse';
